import {
    SET_TRACKED_CAMERA_OPTIONS,
    SET_MAP_LOADED,
    SET_MAP_POSITION,
    SET_DRAW_MODULE,
    SET_MAP_GLOBAL_PADDING,
    SET_MAP_FIT_BOUNDS,
    SET_MAP_PROPERTIES,
    SET_DRAW_FEATURES,
    SET_DRAWN_FEATURES,
    SET_SAVED_DRAWN_FEATURES,
    SET_DRAWN_FEATURE_COORDINATES,
    SET_DRAWING_FEATURE,
    SET_REFRESH_MAP,
    SET_DRAWING_MODULE_IDLE,
    SET_DRAWING_MODULE_MODE,
    SET_SHAPE_FEATURE_PROPERTY,
    SAVE_DRAWN_FEATURE_REQUEST,
    SAVE_DRAWN_FEATURE_SUCCESS,
    SAVE_DRAWN_FEATURE_FAILURE,
    FETCH_DRAWN_FEATURES_REQUEST,
    FETCH_DRAWN_FEATURES_SUCCESS,
    FETCH_DRAWN_FEATURES_FAILURE,
    UPDATE_DRAWN_FEATURE_REQUEST,
    UPDATE_DRAWN_FEATURE_SUCCESS,
    UPDATE_DRAWN_FEATURE_FAILURE,
    DELETE_DRAWN_FEATURE_REQUEST,
    DELETE_DRAWN_FEATURE_SUCCESS,
    DELETE_DRAWN_FEATURE_FAILURE,
    CLEAR_SAVED_DRAWN_FEATURES
} from './Types';
import { IMapAction, IMapReducerState } from './mapTypes';

const defaultState = {
    isSavingDrawing: false,
    isFetchingDrawing: false,
    isDeletingDrawing: false,
    error: false,
    mapPosition: null,
    drawModule: null,
    drawnFeatures: {
        type: 'FeatureCollection',
        features: []
    },
    savedDrawnFeatures: [],
    drawingFeature: null,
    isDrawing: false,
    drawMode: null,
    mapProperties: null,
    refresh: false,
    isMapLoaded: false,
    cameraOptions: null,
    fitBounds: undefined,
    fitBoundsOptions: undefined,
    globalPadding: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0
    }
    // mapProperties example values
    // mapProperties: {
    //     fitBounds: [[-1.797358, 51.559699][-1.780895, 51.565094]],
    //     fitBoundsOptions: {
    //         padding: 0,
    //         pitch: 0,
    //         bearing: 0
    //     },
    //     center: [-1.790646, 51.562726], // The Carto Group HQ, Swindon
    //     zoom: [4],
    //     pitch: [0],
    //     bearing: [0]
    // }
};

function pushSavedDrawnFeaturesToArray(
    state: IMapReducerState,
    action: IMapAction
): IMapReducerState {
    const savedDrawnFeatures = [...state.savedDrawnFeatures];
    const newFeature = action.savedDrawnFeatures; // Must be received as an array of objects

    // Check if the new feature already exists in the array
    const existingIndex = savedDrawnFeatures.findIndex(
        f => f?.properties.id === newFeature[0]?.properties?.id
    );

    // If the new feature already exists, replace it in the array
    if (existingIndex !== -1) {
        savedDrawnFeatures[existingIndex] = newFeature[0];
    } else {
        // If the new feature doesn't exist, add it to the array
        savedDrawnFeatures.push(...newFeature);
    }

    return { ...state, savedDrawnFeatures };
}

const setDrawnFeatureCoordinates = (
    rawState: IMapReducerState,
    action: IMapAction
): IMapReducerState => {
    const state = { ...rawState };
    const featureIdx = state.drawnFeatures.features.findIndex(f => f.id === action.id);
    const feature = state.drawnFeatures.features[featureIdx];
    feature.geometry.coordinates = action.coordinates;
    state.drawnFeatures.features.splice(featureIdx, 1, feature);
    return state;
};

export default function higherOrderReducer(name = '') {
    return function reducer(
        state: IMapReducerState = defaultState,
        action: IMapAction
    ): IMapReducerState {
        if (action.name !== name) return state;
        switch (action.type) {
            case SET_TRACKED_CAMERA_OPTIONS:
                return { ...state, cameraOptions: action.cameraOptions };

            case SET_MAP_LOADED:
                return { ...state, isMapLoaded: action.isLoaded };
            case SET_MAP_POSITION:
                return { ...state, mapPosition: action.mapPosition };
            case SET_DRAW_MODULE:
                return { ...state, drawModule: action.drawModule };
            case SET_MAP_GLOBAL_PADDING:
                return { ...state, globalPadding: action.padding };
            case SET_MAP_FIT_BOUNDS:
                return {
                    ...state,
                    fitBounds: action.bounds,
                    fitBoundsOptions: {
                        padding: state.globalPadding,
                        ...action.options
                    }
                };
            case SET_MAP_PROPERTIES:
                return {
                    ...state,
                    mapProperties: {
                        ...state.mapProperties,
                        ...action.properties
                        // fitBoundsOptions: {
                        //     ...(state.mapProperties ? state.mapProperties.fitBoundsOptions : {}),
                        //     ...action.properties.fitBoundsOptions
                        // }
                    }
                };
            case SET_DRAW_FEATURES:
                return {
                    ...state,
                    drawnFeatures: { ...action.drawnFeatures },
                    drawingFeature: action.selectedFeature
                };
            case SET_DRAWN_FEATURES:
                return { ...state, drawnFeatures: { ...action.drawnFeatures } };
            case SET_DRAWN_FEATURE_COORDINATES:
                return setDrawnFeatureCoordinates(state, action);
            case SET_DRAWING_FEATURE:
                return { ...state, drawingFeature: action.drawingFeature };
            case SET_REFRESH_MAP:
                return {
                    ...state,
                    refresh: action.value
                };
            case SET_DRAWING_MODULE_IDLE:
                return {
                    ...state,
                    isDrawing: false,
                    drawMode: null
                };
            case SET_DRAWING_MODULE_MODE:
                return {
                    ...state,
                    isDrawing: true,
                    drawMode: action.mode
                };
            case SET_SHAPE_FEATURE_PROPERTY:
                return {
                    ...state,
                    drawnFeatures: { ...action.drawnFeatures },
                    drawingFeature: action.selectedFeature
                };
            case SET_SAVED_DRAWN_FEATURES:
                return pushSavedDrawnFeaturesToArray(state, action);
            case CLEAR_SAVED_DRAWN_FEATURES:
                return { ...state, savedDrawnFeatures: [] };
            case SAVE_DRAWN_FEATURE_REQUEST:
                return { ...state, isSavingDrawing: true, error: false };
            case SAVE_DRAWN_FEATURE_SUCCESS:
                return { ...state, isSavingDrawing: false };
            case SAVE_DRAWN_FEATURE_FAILURE:
                return { ...state, isSavingDrawing: false, error: true };
            case FETCH_DRAWN_FEATURES_REQUEST:
                return { ...state, isFetchingDrawing: true, error: false };
            case FETCH_DRAWN_FEATURES_SUCCESS:
                return { ...state, isFetchingDrawing: false };
            case FETCH_DRAWN_FEATURES_FAILURE:
                return { ...state, isFetchingDrawing: false, error: true };
            case UPDATE_DRAWN_FEATURE_REQUEST:
                return { ...state, isSavingDrawing: true, error: false };
            case UPDATE_DRAWN_FEATURE_SUCCESS:
                return { ...state, isSavingDrawing: false };
            case UPDATE_DRAWN_FEATURE_FAILURE:
                return { ...state, isSavingDrawing: false, error: true };
            case DELETE_DRAWN_FEATURE_REQUEST:
                return { ...state, isDeletingDrawing: true };
            case DELETE_DRAWN_FEATURE_SUCCESS:
                return { ...state, isDeletingDrawing: false };
            case DELETE_DRAWN_FEATURE_FAILURE:
                return { ...state, isDeletingDrawing: false, error: true };
            default:
                return state;
        }
    };
}
